// cartStore.ts
import { PriceInfo, ProductInformation } from "@/data/client";
import { defineStore } from "pinia";
import { CartItem } from "@/interface/CartItem";
import Decimal from "decimal.js";
import { SelectedArtWork } from "@/interface/SelectedArtWork";
import {
  findPriceRange,
  getEffectiveDiscountPercent,
} from "@/helpers/pricingHelpers";

export const useCartStore = defineStore("cart", {
  state: () => ({
    totalPrice: 2000,
    totalDiscount: 0,
    totalOrderPrice: 0,
    cart: [] as CartItem[],
    addedToCart: false,
  }),
  actions: {
    addToCart(
      product: ProductInformation,
      qty: number,
      itemDiscount: number,
      artworkSelections: Record<string, SelectedArtWork>,
      comment: string,
      reqDate: Date | undefined
    ) {
      const existingCartItem = this.cart.find(
        (item) => item.product.itemId === product.itemId
      );

      if (existingCartItem) {
        // Check quantity and check if there is a new discount
        const newQuantity = existingCartItem.quantity + qty;

        const priceRow = findPriceRange(
          newQuantity,
          existingCartItem.product.priceRules!
        );

        if (priceRow) {
          existingCartItem.quantity = newQuantity;
          existingCartItem.price = new Decimal(priceRow.price!);

          const effectiveDiscount = getEffectiveDiscountPercent(
            existingCartItem.product,
            newQuantity,
            priceRow
          );
          existingCartItem.discount = effectiveDiscount;
        }
      } else {
        const priceRow = findPriceRange(qty, product.priceRules!);

        const effectiveDiscount = getEffectiveDiscountPercent(
          product,
          qty,
          priceRow!
        );

        this.cart.push({
          product: product,
          quantity: qty,
          discount: effectiveDiscount,
          price: new Decimal(priceRow!.price!),
          artWorkSelections: artworkSelections,
          comment: comment,
          requestedDeliveryDate: reqDate,
        });
      }
      this.addedToCart = true;
      setTimeout(() => {
        this.addedToCart = false;
      }, 700);

      this.calculateTotals();
    },
    updateCartItem(
      product: ProductInformation,
      qty: number,
      itemDiscount: number,
      artWorkSelections: Record<string, SelectedArtWork>,
      comment: string,
      reqDate: Date | undefined
    ) {
      const existingCartItem = this.cart.find(
        (item) => item.product.itemId === product.itemId
      );

      if (existingCartItem) {
        existingCartItem.comment = comment;
        existingCartItem.artWorkSelections = artWorkSelections;
        existingCartItem.requestedDeliveryDate = reqDate;

        // Check quantity and check if there is a new discount
        const newQuantity = qty;

        const priceRow = findPriceRange(
          newQuantity,
          existingCartItem.product.priceRules!
        );
        if (priceRow) {
          existingCartItem.quantity = newQuantity;
          existingCartItem.price = new Decimal(priceRow.price!);

          const effectiveDiscount = getEffectiveDiscountPercent(
            existingCartItem.product,
            newQuantity,
            priceRow
          );
          existingCartItem.discount = effectiveDiscount;
        }
      } else {
        const priceRow = findPriceRange(qty, product.priceRules!);
        const effectiveDiscount = getEffectiveDiscountPercent(
          product,
          qty,
          priceRow!
        );
        this.cart.push({
          product: product,
          quantity: qty,
          discount: effectiveDiscount,
          price: new Decimal(priceRow!.price!),
          artWorkSelections: artWorkSelections,
          comment: comment,
          requestedDeliveryDate: reqDate,
        });
      }
      this.calculateTotals();
    },
    removeCartItem(product: ProductInformation) {
      this.cart = this.cart.filter(
        (item) => item.product.itemId !== product.itemId
      );
      this.calculateTotals();
    },
    calculateTotals() {
      this.totalPrice = 0;
      this.totalDiscount = 0;
      this.totalOrderPrice = 0;

      this.cart.forEach((item) => {
        const itemSum = item.price!.toNumber() * item.quantity;
        this.totalPrice += itemSum;
        this.totalDiscount += (itemSum * item.discount) / 100;
      });

      this.totalOrderPrice = this.totalPrice - this.totalDiscount;
    },
    emptyCart() {
      this.cart = [];
    },
    hasAnyItems(): boolean {
      return this.cart && this.cart.length > 0;
    },
  },
});
